import React, { useState, useEffect, useRef } from 'react';
import Plot from 'react-plotly.js';
import { getChartTitle, getChartAxis } from '../../utils/chartColors';

/**
 * Premium/Discount Chart Component
 * Shows premium (green bars above 0) and discount (red bars below 0)
 * @param {Array} data - Array of data objects with date and premium_discount
 * @param {string} xKey - Key for x-axis data (default: 'date')
 * @param {string} title - Chart title
 * @param {string} xLabel - X-axis label
 * @param {string} yLabel - Y-axis label
 * @param {number} height - Chart height
 */
const PremiumDiscountChart = ({ 
  data = [], 
  xKey = 'date',
  title = 'Daily Premium/Discount %',
  xLabel = 'Date',
  yLabel = 'Premium/Discount %',
  height, // Optional: if not provided, will use container height
  premiumColor = '#10b981', // Default green
  discountColor = '#ef4444'  // Default red
}) => {
  const [themeKey, setThemeKey] = useState(0);
  const [containerHeight, setContainerHeight] = useState(height);
  const containerRef = useRef(null);
  const [isLightMode, setIsLightMode] = useState(() => {
    return document.body.classList.contains('light-mode') || 
           document.querySelector('.landing-page')?.classList.contains('light-mode');
  });

  // Update container height when container resizes or height prop changes
  useEffect(() => {
    // If height prop is provided, use it directly
    if (height) {
      setContainerHeight(height);
      return;
    }

    // Otherwise, track container height using ResizeObserver
    const updateHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // Use computed height, or getBoundingClientRect height, or parent height
        const computedHeight = window.getComputedStyle(containerRef.current).height;
        const heightValue = rect.height > 0 ? rect.height : 
                          (computedHeight && computedHeight !== 'auto' ? parseFloat(computedHeight) : null);
        
        if (heightValue && heightValue > 0) {
          setContainerHeight(heightValue);
        } else {
          // Fallback: try parent container
          const parent = containerRef.current.parentElement;
          if (parent) {
            const parentRect = parent.getBoundingClientRect();
            if (parentRect.height > 0) {
              setContainerHeight(parentRect.height);
            }
          }
        }
      }
    };

    // Update on mount with a small delay to ensure DOM is ready
    setTimeout(updateHeight, 0);
    
    // Also update after a short delay for initial render
    setTimeout(updateHeight, 100);

    // Debounce resize events
    let resizeTimeout;
    const debouncedUpdateHeight = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateHeight, 10);
    };

    // Use ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(debouncedUpdateHeight);
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also listen to window resize
    window.addEventListener('resize', debouncedUpdateHeight);

    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
      window.removeEventListener('resize', debouncedUpdateHeight);
    };
  }, [height]);

  // Listen for theme changes
  useEffect(() => {
    const checkThemeChange = () => {
      setThemeKey(prev => prev + 1);
      setIsLightMode(
        document.body.classList.contains('light-mode') || 
        document.querySelector('.landing-page')?.classList.contains('light-mode')
      );
    };

    // Check immediately
    checkThemeChange();

    // Listen for DOM mutations (theme class changes)
    const observer = new MutationObserver(checkThemeChange);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    const landingPage = document.querySelector('.landing-page');
    if (landingPage) {
      observer.observe(landingPage, { attributes: true, attributeFilter: ['class'] });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Helper function to interpolate missing values and fill in weekend gaps
  const interpolatePremiumDiscountData = (data) => {
    if (data.length < 2) return data;
    
    // First convert premium_discount to percentage
    const convertedData = data.map(item => ({
      ...item,
      premium_discount_pct: item.premium_discount != null ? item.premium_discount * 100 : 0
    }));
    
    const sortedData = [...convertedData].sort((a, b) => new Date(a[xKey]) - new Date(b[xKey]));
    const interpolatedData = [];
    
    for (let i = 0; i < sortedData.length; i++) {
      const current = sortedData[i];
      
      // If current data point has valid premium/discount, use it
      if (current.premium_discount_pct != null && current.premium_discount_pct !== 0) {
        interpolatedData.push(current);
      } else {
        // Try to find the nearest valid data points for interpolation
        let prevValid = null;
        let nextValid = null;
        
        // Look backwards for the last valid data point
        for (let j = i - 1; j >= 0; j--) {
          if (sortedData[j].premium_discount_pct != null && sortedData[j].premium_discount_pct !== 0) {
            prevValid = sortedData[j];
            break;
          }
        }
        
        // Look forwards for the next valid data point
        for (let j = i + 1; j < sortedData.length; j++) {
          if (sortedData[j].premium_discount_pct != null && sortedData[j].premium_discount_pct !== 0) {
            nextValid = sortedData[j];
            break;
          }
        }
        
        // If we have both previous and next valid points, interpolate
        if (prevValid && nextValid) {
          const prevDate = new Date(prevValid[xKey]);
          const currentDate = new Date(current[xKey]);
          const nextDate = new Date(nextValid[xKey]);
          
          // Calculate interpolation factor (0 = prevValid, 1 = nextValid)
          const totalDays = (nextDate - prevDate) / (1000 * 60 * 60 * 24);
          const currentDays = (currentDate - prevDate) / (1000 * 60 * 60 * 24);
          const factor = totalDays > 0 ? currentDays / totalDays : 0;
          
          // Interpolate premium/discount
          const interpolatedValue = prevValid.premium_discount_pct + 
            (nextValid.premium_discount_pct - prevValid.premium_discount_pct) * factor;
          
          interpolatedData.push({
            ...current,
            premium_discount_pct: interpolatedValue
          });
        } else if (prevValid) {
          // If only previous valid point, use forward fill
          interpolatedData.push({
            ...current,
            premium_discount_pct: prevValid.premium_discount_pct
          });
        } else if (nextValid) {
          // If only next valid point, use backward fill
          interpolatedData.push({
            ...current,
            premium_discount_pct: nextValid.premium_discount_pct
          });
        } else {
          // If no valid points found, keep as is
          interpolatedData.push(current);
        }
      }
    }
    
    return interpolatedData;
  };

  // Interpolate missing values and fill in weekend gaps
  const processedData = interpolatePremiumDiscountData(data);

  // Separate positive (premium) and negative (discount) values
  const premiumData = processedData.map(item => ({
    x: item[xKey],
    y: item.premium_discount_pct >= 0 ? item.premium_discount_pct : 0
  }));

  const discountData = processedData.map(item => ({
    x: item[xKey],
    y: item.premium_discount_pct < 0 ? item.premium_discount_pct : 0
  }));

  const plotData = [
    {
      x: premiumData.map(d => d.x),
      y: premiumData.map(d => d.y),
      type: 'bar',
      name: 'Premium',
      marker: {
        color: premiumColor,
        opacity: 0.8
      },
      hovertemplate: `<b>Premium</b><br>${xLabel}: %{x}<br>${yLabel}: %{y:.2f}%<extra></extra>`
    },
    {
      x: discountData.map(d => d.x),
      y: discountData.map(d => d.y),
      type: 'bar',
      name: 'Discount',
      marker: {
        color: discountColor,
        opacity: 0.8
      },
      hovertemplate: `<b>Discount</b><br>${xLabel}: %{x}<br>${yLabel}: %{y:.2f}%<extra></extra>`
    }
  ];

  const layout = {
    title: getChartTitle(title, 20),
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: isLightMode ? 'rgba(0,0,0,0)' : 'rgba(15,15,15,0.5)',
    xaxis: {
      ...getChartAxis(xLabel, true),
      showgrid: true,
      nticks: 8,
      tickmode: 'auto'
    },
    yaxis: {
      ...getChartAxis(yLabel, false),
      showgrid: true,
      zeroline: true,
      zerolinecolor: 'rgba(255,255,255,0.3)',
      zerolinewidth: 2
    },
    barmode: 'relative', // Stack bars to show both premium and discount
    bargap: 0.1,
    margin: (() => {
      const isMobile = window.innerWidth <= 768;
      return {
        l: 80,
        r: isMobile ? 40 : 120,
        t: 80,
        b: isMobile ? 120 : 40
      };
    })(),
    autosize: true,
    height: containerHeight || 400, // Use container height or default to 400
    legend: (() => {
      const isMobile = window.innerWidth <= 768;
      const itemCount = 2; // Premium and Discount
      
      if (isMobile) {
        return {
          orientation: 'h',
          x: 0.5,
          xanchor: 'center',
          y: itemCount > 6 ? -0.25 : -0.2,
          yanchor: 'top',
          font: { 
            size: itemCount > 6 ? 9 : 10, 
            color: isLightMode ? '#000000' : '#ffffff',
            family: "'TWK Everett', sans-serif"
          },
          tracegroupgap: 10
        };
      }
      
      return {
        orientation: 'v',
        x: 1.02,
        xanchor: 'left',
        y: 1,
        yanchor: 'top',
        font: { 
          size: 12, 
          color: isLightMode ? '#000000' : '#ffffff',
          family: "'TWK Everett', sans-serif"
        }
      };
    })(),
    hovermode: 'x unified',
    hoverlabel: {
      bgcolor: 'rgba(0, 0, 0, 0.8)',
      bordercolor: 'rgba(255, 255, 255, 0.2)',
      font: { 
        size: 11, 
        color: '#ffffff',
        family: "'TWK Everett', sans-serif"
      }
    }
  };

  const config = {
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
    responsive: true,
    useResizeHandler: true,
    toImageButtonOptions: {
      format: 'png',
      filename: title.toLowerCase().replace(/\s+/g, '_'),
      height: height,
      width: 800,
      scale: 2
    }
  };

  return (
    <div 
      ref={containerRef}
      className="premium-discount-chart" 
      style={{ width: '100%', height: '100%', minHeight: '400px', position: 'relative' }}
    >
      <Plot
        key={themeKey}
        data={plotData}
        layout={layout}
        config={config}
        style={{ width: '100%', height: '100%', minHeight: '400px' }}
        useResizeHandler={true}
      />
    </div>
  );
};

export default PremiumDiscountChart;